/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2018 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { Component } from 'react';
import { deepEqual } from '@instructure/ui-utils';
import { logWarn as warn } from '@instructure/console';
import { requestAnimationFrame } from '@instructure/ui-dom-utils';
import { allowedProps } from "./props.js";
/**
---
category: components
---
**/
class Editable extends Component {
  constructor(_props) {
    super(_props);
    this.state = {
      showModeToggle: false,
      valueOnEdit: null // the value when mode flips from view -> edit
    };
    this._editorRef = null;
    this._editButtonRef = null;
    this.handleEditESC = event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.enterView();
      }
    };
    this.handleViewMouseOver = event => {
      if (this.props.mode === 'view') {
        // because the mouse event handlers are on the container, not the view
        event.stopPropagation();
        this.setState({
          showModeToggle: true
        });
      }
    };
    this.handleViewMouseOut = event => {
      if (this.props.mode === 'view') {
        event.stopPropagation();
        this.setState({
          showModeToggle: document.activeElement === this._editButtonRef
        });
      }
    };
    this.handleViewClick = event => {
      if (event.buttons === 1) {
        requestAnimationFrame(() => {
          this.enterEdit();
        });
      }
    };
    this.handleEditBlur = () => {
      this.enterView();
    };
    this.handleEditButtonFocus = () => {
      this.setState({
        showModeToggle: true
      });
    };
    this.handleEditButtonBlur = () => {
      this.setState({
        showModeToggle: false
      });
    };
    this.handleEditButtonClick = () => {
      this.enterEdit();
    };
    this.ref = null;
    this.handleContainerRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    // ------- prop getters ------
    this.getContainerProps = (props = {}) => {
      const _this$props = this.props,
        mode = _this$props.mode,
        readOnly = _this$props.readOnly;
      return {
        onMouseOver: this.handleViewMouseOver,
        onMouseOut: this.handleViewMouseOut,
        onMouseDown: mode !== 'edit' ? this.handleViewClick : void 0,
        onKeyUp: mode === 'edit' ? this.handleEditESC : void 0,
        readOnly,
        ...props,
        ref: this.handleContainerRef
      };
    };
    this.getViewerProps = (props = {}) => {
      return {
        mode: this.props.mode,
        readOnly: this.props.readOnly,
        ...props
      };
    };
    this.getEditorProps = (props = {}) => {
      return {
        mode: this.props.mode,
        onBlur: this.handleEditBlur,
        editorRef: el => {
          this._editorRef = el;
        },
        readOnly: this.props.readOnly,
        ...props
      };
    };
    this.getEditButtonProps = (props = {}) => {
      return {
        onClick: this.handleEditButtonClick,
        onFocus: this.handleEditButtonFocus,
        onBlur: this.handleEditButtonBlur,
        isVisible: this.state.showModeToggle,
        buttonRef: el => {
          this._editButtonRef = el;
        },
        readOnly: this.props.readOnly,
        ...props
      };
    };
    warn(_props.readOnly ? _props.mode === 'view' : true, '[Editable] When readOnly is true, mode must be "view"');
  }
  componentDidMount() {
    if (this.props.mode === 'edit') {
      this.focusEditor();
    }
  }
  componentDidUpdate(prevProps) {
    const _this$props2 = this.props,
      mode = _this$props2.mode,
      value = _this$props2.value,
      onChange = _this$props2.onChange;

    // on the switch from view -> edit
    if (prevProps.mode !== 'edit' && mode === 'edit') {
      this.setState({
        valueOnEdit: value
      });
      this.focusEditor();
    }

    // on the switch from edit to view
    if (prevProps.mode === 'edit' && mode !== 'edit') {
      this._editButtonRef && this._editButtonRef.focus();
      if (onChange && !deepEqual(this.state.valueOnEdit, value)) {
        onChange(value);
      }
    }
  }
  focusEditor() {
    warn(!!this._editorRef && !this.props.readOnly, '[Editable] Did you forget to connect editorRef to your editor component?');
    if (this._editorRef) {
      this._editorRef.focus();
    }
  }
  enterView() {
    if (this.props.mode !== 'view') {
      this.setState({
        showModeToggle: true
      });
      this.props.onChangeMode('view');
    }
  }
  enterEdit() {
    if (!this.props.readOnly) {
      this.setState({
        showModeToggle: false
      });
      if (this.props.mode !== 'edit') {
        this.props.onChangeMode('edit');
      }
    }
  }
  render() {
    const _this$props3 = this.props,
      children = _this$props3.children,
      _this$props3$render = _this$props3.render,
      render = _this$props3$render === void 0 ? children : _this$props3$render,
      mode = _this$props3.mode;
    if (typeof render === 'function') {
      return render({
        mode,
        getContainerProps: this.getContainerProps,
        getViewerProps: this.getViewerProps,
        getEditorProps: this.getEditorProps,
        getEditButtonProps: this.getEditButtonProps
      });
    } else {
      return null;
    }
  }
}
Editable.displayName = "Editable";
Editable.allowedProps = allowedProps;
Editable.defaultProps = {
  readOnly: false,
  children: null
};
export default Editable;
export { Editable };